import type { BrokerAccount, Cashflow, RiskMetrics } from "@/data/mock-data";
import { computeChainLinkedTWR } from "@/lib/whb-accounts-adapter";

/**
 * Sentinel id for the virtual "All Live Accounts" account. Kept in its
 * own module so the selector, the page dispatch, and the aggregation
 * helper can all refer to it without circular imports.
 */
export const ALL_LIVE_ACCOUNT_ID = "__all_live__";

/** True iff `id` refers to the virtual All-Live-Accounts view. */
export function isAllLiveAccountId(id: string): boolean {
  return id === ALL_LIVE_ACCOUNT_ID;
}

/**
 * Build a virtual `BrokerAccount`-shaped object that represents the
 * consolidated view across every Live account.
 *
 * Strategy summary (this is a demo, not a production risk-engine —
 * close-enough numbers in the right direction are the goal):
 *
 *  • Dollar fields (`portfolioValue`, `stocksValue`, `cashValue`,
 *    `buyingPower*`, `allTimeChange`) sum linearly — they are
 *    additively decomposable.
 *  • `stocksCount` sums too. This over-counts if two accounts hold the
 *    same ticker (e.g. both long SPY), but the real API has the same
 *    behaviour in its "number of positions" widget so users won't be
 *    surprised.
 *  • `stocksDayChangePct` and `allTimeChangePct` are weighted by
 *    `portfolioValue` — gives the user a blended number that matches
 *    what a single consolidated portfolio would look like.
 *  • `equityCurve` sums pointwise, assuming all accounts share the
 *    same `curveDates` (they do, since mock-data generates them with
 *    the same daily window ending today). If lengths diverge we align
 *    on the shortest series.
 *  • `twrCurve` is recomputed from the summed equity curve — this is
 *    the same "normalize to 0 at start" treatment the per-account
 *    TWR uses, so chart shapes are comparable.
 *  • `spyCurve` / `spyTotalCurve` — every account was generated with
 *    a different seed (so the SPY curves differ slightly), but for
 *    the aggregate view we take the first account's SPY series as the
 *    canonical benchmark. Averaging them would give a smoother curve
 *    that doesn't actually correspond to any real SPY trajectory.
 *  • `risk` metrics are `portfolioValue`-weighted averages. WPR is
 *    recomputed from the aggregated TWR curve so it's exact.
 *  • `cashflows` are merged and sorted by date so deposit/withdrawal
 *    dots plot on the chart in temporal order.
 *  • `botIds` is the union of all member accounts' bot ids.
 *
 * Returns `null` if no live accounts exist — the caller should hide
 * the "All Live Accounts" selector entry in that case.
 */
export function aggregateLiveAccounts(
  accounts: BrokerAccount[]
): BrokerAccount | null {
  const live = accounts.filter((a) => !a.paper);
  if (live.length === 0) return null;

  // --- Curves --------------------------------------------------------------

  // Pick the aggregate window to be the UNION of every account's
  // window — i.e. the earliest inception across all live accounts.
  // Previously we used the INTERSECTION (shortest common length),
  // which truncated All-Live's ALL range to the youngest account's
  // inception and hid months of older history from accounts like
  // Default Live (opened 2024-03-18) when Ellen (opened 2024-10-30)
  // joined. Accounts that didn't exist yet on a given day contribute
  // $0 to that day's aggregate, which is the accurate answer.
  //
  // Algorithm: pick the account with the longest curveDates as the
  // "master" date axis. For every other account, align its shorter
  // curve right-edge-aligned (their latest day = master's latest
  // day) and left-pad with zeros for days before they existed.
  let master: BrokerAccount = live[0]!;
  for (const a of live) {
    if (a.curveDates.length > master.curveDates.length) master = a;
  }
  const curveDates = master.curveDates.slice();
  const maxLen = curveDates.length;
  const equityCurve: number[] = new Array(maxLen).fill(0);
  for (const a of live) {
    // Right-align each account's equity curve against the master
    // axis. `a.equityCurve[0]` maps to master date index
    // (maxLen - a.equityCurve.length), which is zero or positive.
    const pad = maxLen - a.equityCurve.length;
    for (let i = 0; i < a.equityCurve.length; i++) {
      equityCurve[pad + i] += a.equityCurve[i]!;
    }
  }
  for (let i = 0; i < maxLen; i++) {
    equityCurve[i] = +equityCurve[i]!.toFixed(2);
  }

  // SPY series — also take from the master (it's the account whose
  // window covers everything, so its SPY is the longest available).
  const spyCurve = master.spyCurve.slice();
  const spyTotalCurve = master.spyTotalCurve.slice();
  const spyReturnPct = master.spyReturnPct;

  // --- Cashflows ----------------------------------------------------------

  const cashflows: Cashflow[] = live
    .flatMap((a) => a.cashflows)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));

  // TWR — chain-linked from the summed equity curve + merged cashflows.
  //
  // The naive `(equity[i] / equity[0]) − 1` formula double-counts any
  // deposit as a positive return (the equity curve jumps, the formula
  // attributes the jump to price action). A $50k deposit that hits a
  // $100k aggregate would read as a ~+50% "return". That's the
  // spike Sergey flagged coinciding with a cashflow dot on All-Live.
  //
  // `computeChainLinkedTWR` subtracts cashflows from the ending
  // equity before computing each period return, so deposits /
  // withdrawals are neutralised exactly the way institutional TWR
  // does it. The result is cashflow-neutral %, comparable to what
  // individual accounts show.
  const twrCurve = computeChainLinkedTWR(equityCurve, curveDates, cashflows);

  // --- Dollar sums --------------------------------------------------------

  const sum = (pick: (a: BrokerAccount) => number) =>
    live.reduce((s, a) => s + pick(a), 0);

  const portfolioValue = sum((a) => a.portfolioValue);
  const stocksValue = sum((a) => a.stocksValue);
  const cashValue = sum((a) => a.cashValue);
  const buyingPower = sum((a) => a.buyingPower);
  const buyingPowerExBots = sum((a) => a.buyingPowerExBots);
  const stocksCount = sum((a) => a.stocksCount);
  const allTimeChange = sum((a) => a.allTimeChange);

  // --- Weighted percent fields -------------------------------------------

  // Weight by portfolioValue — falls back to equal weighting when total
  // is zero (e.g. brand-new accounts). This keeps the result finite in
  // degenerate data.
  const weightedPct = (pick: (a: BrokerAccount) => number) => {
    if (portfolioValue === 0) {
      return live.reduce((s, a) => s + pick(a), 0) / live.length;
    }
    return live.reduce(
      (s, a) => s + pick(a) * (a.portfolioValue / portfolioValue),
      0
    );
  };

  const stocksDayChangePct = weightedPct((a) => a.stocksDayChangePct);
  const allTimeChangePct = weightedPct((a) => a.allTimeChangePct);
  const targetApyPct = weightedPct((a) => a.targetApyPct);

  // --- Risk metrics bundle -----------------------------------------------

  // WPR: recompute from the aggregate TWR curve so it's internally
  // consistent with the chart. Everything else is portfolio-weighted
  // from per-account metrics — good enough for a demo where the user
  // is mostly glancing at directional colour + magnitude.
  const aggregatedWprPct = twrCurve[twrCurve.length - 1] ?? 0;
  const risk: RiskMetrics = {
    wprPct: aggregatedWprPct,
    wprSpyPct: weightedPct((a) => a.risk.wprSpyPct),
    wprSpyTotalPct: weightedPct((a) => a.risk.wprSpyTotalPct),
    apyPct: weightedPct((a) => a.risk.apyPct),
    apySpyPct: weightedPct((a) => a.risk.apySpyPct),
    apySpyTotalPct: weightedPct((a) => a.risk.apySpyTotalPct),
    volPct: weightedPct((a) => a.risk.volPct),
    volSpyPct: weightedPct((a) => a.risk.volSpyPct),
    volSpyTotalPct: weightedPct((a) => a.risk.volSpyTotalPct),
    maxDDPct: weightedPct((a) => a.risk.maxDDPct),
    maxDDSpyPct: weightedPct((a) => a.risk.maxDDSpyPct),
    maxDDSpyTotalPct: weightedPct((a) => a.risk.maxDDSpyTotalPct),
    beta: weightedPct((a) => a.risk.beta),
    alphaPct: weightedPct((a) => a.risk.alphaPct),
    sharpe: weightedPct((a) => a.risk.sharpe),
    sharpeSpy: weightedPct((a) => a.risk.sharpeSpy),
    sharpeSpyTotal: weightedPct((a) => a.risk.sharpeSpyTotal),
    calmar: weightedPct((a) => a.risk.calmar),
    calmarSpy: weightedPct((a) => a.risk.calmarSpy),
    calmarSpyTotal: weightedPct((a) => a.risk.calmarSpyTotal)
  };

  // --- Bot ids ------------------------------------------------------------

  const botIds = Array.from(new Set(live.flatMap((a) => a.botIds)));

  // If ANY member account is on the total-equity basis (i.e. their
  // equity curve already reflects cashflows), propagate that to the
  // aggregate so the chart doesn't re-layer cashflows on top and
  // double-count deposits. Real accounts (source: "real") all use
  // "total-equity"; demo accounts use "market-only". Mixing isn't
  // expected in practice — the selector shows either "All Live
  // (real)" or demo-only — but if it ever happens, total-equity wins
  // because the per-account curves are already at total-equity.
  const portfolioBasis: "market-only" | "total-equity" =
    live.some((a) => a.portfolioBasis === "total-equity")
      ? "total-equity"
      : "market-only";

  return {
    id: ALL_LIVE_ACCOUNT_ID,
    name: "All Live Accounts",
    provider: `${live.length} live ${live.length === 1 ? "account" : "accounts"}`,
    // Empty masked — it's not a real broker account, and showing "*XXXX"
    // next to "All Live Accounts" would be misleading.
    masked: "",
    // `paper: false` so the page's footer disclaimer (and any other
    // paper-gated UI) doesn't show — this view is strictly live-only.
    paper: false,
    portfolioValue,
    buyingPower,
    buyingPowerExBots,
    stocksValue,
    cashValue,
    stocksCount,
    stocksDayChangePct,
    allTimeChange,
    allTimeChangePct,
    spyReturnPct,
    twrCurve,
    spyCurve,
    spyTotalCurve,
    curveDates,
    risk,
    targetApyPct,
    cashflows,
    equityCurve,
    portfolioBasis,
    botIds
  };
}
